import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import ReportsChart from "./ReportsChart";
import ReportsPieChart from "./ReportsPieChart";
// import "./ReportsMain.css";

interface ReportsFilters {
  jobType: string;
  location: string;
  fromDate: string;
  toDate: string;
}

export default function ReportsMain() {
  const { t } = useTranslation("settings");
  const { callGetJobAllStatusCounts, callGetEmployerJobs, callGetAppliedJobsLastSevenDays } = useEmployerJobApi();
  const userDetails = useAppUserDetailsSelector();
  const employerId = userDetails?.user?.company_id;

  console.log("ReportsMain - userDetails:", userDetails);
  console.log("ReportsMain - employerId:", employerId);

  const [filters, setFilters] = useState<ReportsFilters>({
    jobType: "",
    location: "",
    fromDate: "",
    toDate: "",
  });

  const [jobStatusFilter, setJobStatusFilter] = useState<string>("");
  const [filteredJobs, setFilteredJobs] = useState<any[]>([]);
  const [reportsData, setReportsData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [lineChartData, setLineChartData] = useState<
    Array<{
      date: string;
      personalInterview: number;
    }>
  >([]);

  const pieChartRef = useRef<HTMLDivElement>(null);
  const lineChartRef = useRef<HTMLDivElement>(null);

  // Load applied jobs for line chart
  const loadAppliedJobsDayWise = () => {
    const params: any = {};
    console.log(filters);

    // Handle job_ids based on filter conditions
    if (filters.jobType) {
      // If a specific job is selected, use only that job ID
      params.job_ids = [filters.jobType];
    } else if (jobStatusFilter) {
      // If job status filter is active but no specific job selected
      if (filteredJobs.length > 0) {
        // Send all filtered job IDs
        params.job_ids = filteredJobs.map((job: any) => job.emp_job_id);
      } else if (jobStatusFilter === "expired") {
        // For expired with no jobs found, send ["MMMMM"]
        params.job_ids = ["MMMMM"];
      }
    }

    // Add date range if provided
    if (filters.fromDate) {
      params.from_date = filters.fromDate;
    }
    if (filters.toDate) {
      params.to_date = filters.toDate;
    }
    console.log("Loading applied jobs day wise with params:", params);
    callGetAppliedJobsLastSevenDays(
      params,
      (message: string, apiData: any) => {
        console.log("Applied jobs last seven days for line chart:", apiData);
        if (apiData && apiData.daily_counts && Array.isArray(apiData.daily_counts)) {
          // Transform API data to line chart format
          const transformedData = apiData.daily_counts.map((item: any) => ({
            date: item.date || item.day_name,
            personalInterview: item.count,
          }));
          setLineChartData(transformedData);
        }
      },
      (error: string) => {
        console.error("Failed to load applied jobs data for line chart:", error);
      },
    );
  };

  // Load employer jobs based on job status filter
  useEffect(() => {
    console.log("useEffect triggered - jobStatusFilter:", jobStatusFilter, "employerId:", employerId);

    if (!employerId) {
      console.log("No employerId, skipping API call");
      return;
    }

    setFilteredJobs([]);

    let jobStatuses: string[];
    if (jobStatusFilter === "active") {
      jobStatuses = ["open", "ai_filter"];
    } else if (jobStatusFilter === "expired") {
      jobStatuses = ["expired"];
    } else {
      // Default: load all job statuses on first load and when "all" is selected
      jobStatuses = ["open", "ai_filter", "expired"];
    }

    console.log("Calling API with job_status:", jobStatuses);

    callGetEmployerJobs(
      employerId,
      { job_status: jobStatuses },
      (_message: string, data: any) => {
        console.log("API response received:", data);
        const jobsList = data?.jobs || [];
        setFilteredJobs(Array.isArray(jobsList) ? jobsList : []);
        console.log("Employer jobs loaded successfully. Count:", jobsList.length, "Jobs:", jobsList);
      },
      (error: any) => {
        console.error("Error loading employer jobs:", error);
      },
    );
  }, [jobStatusFilter, employerId]);

  // Load reports data on component mount
  useEffect(() => {
    loadAppliedJobsDayWise();
    loadPieChart();
  }, []);

  // Transform status_counts to pie chart data
  const transformStatusCountsToPieChart = (statusCounts: any) => {
    if (!statusCounts) return [];

    const statusMapping = [
      { key: "rejected", name: t("reports.statuses.rejected"), color: "#f44336" },
      { key: "screened", name: t("reports.statuses.screened"), color: "#9c27b0" },
      { key: "send_ai_interview", name: t("reports.statuses.aiInterview"), color: "#2196f3" },
      { key: "schedule_interview", name: t("reports.statuses.scheduledInterview"), color: "#ff9800" },
      { key: "candidate_accept", name: t("reports.statuses.candidateAccept"), color: "#4caf50" },
      { key: "candidate_reject", name: t("reports.statuses.candidateReject"), color: "#e91e63" },
      { key: "interview_done", name: t("reports.statuses.interviewDone"), color: "#00bcd4" },
      { key: "final_decision", name: t("reports.statuses.finalDecision"), color: "#795548" },
      { key: "hired", name: t("reports.statuses.hired"), color: "#009688" },
      { key: "interview_rejected", name: t("reports.statuses.interviewRejected"), color: "#ff5722" },
    ];

    return statusMapping
      .map((status) => ({
        name: status.name,
        value: statusCounts[status.key] || 0,
        color: status.color,
      }))
      .filter((item) => item.value > 0); // Only show statuses with values > 0
  };

  const pieChartDataToDisplay = reportsData?.status_counts
    ? transformStatusCountsToPieChart(reportsData.status_counts)
    : [];

  const handleFilterChange = (field: keyof ReportsFilters, value: string) => {
    console.log(value);
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilter = () => {
    loadAppliedJobsDayWise();
    loadPieChart();
  };

  const loadPieChart = () => {
    const params: any = {};
    console.log(filters);

    // Handle job_ids based on filter conditions
    if (filters.jobType) {
      // If a specific job is selected, use only that job ID
      params.job_ids = [filters.jobType];
    } else if (jobStatusFilter) {
      // If job status filter is active but no specific job selected
      if (filteredJobs.length > 0) {
        // Send all filtered job IDs
        params.job_ids = filteredJobs.map((job: any) => job.emp_job_id);
      } else if (jobStatusFilter === "expired") {
        // For expired with no jobs found, send ["MMMMM"]
        params.job_ids = ["MMMMM"];
      }
    }

    // Add date range if provided
    if (filters.fromDate) {
      params.from_date = filters.fromDate;
    }
    if (filters.toDate) {
      params.to_date = filters.toDate;
    }

    console.log("Applying filters with params:", params);
    setLoading(true);

    callGetJobAllStatusCounts(
      params,
      (message: string, data: any) => {
        console.log("Reports data received:", data);
        setReportsData(data);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error loading reports:", error);
        setLoading(false);
      },
    );
  };

  const handleExportExcel = () => {
    // Implement Excel export
    console.log("Exporting to Excel");
  };

  const handleExportPDF = async () => {
    try {
      // Dynamically import html2canvas, jsPDF and autoTable
      const html2canvas = (await import("html2canvas" as any)).default;
      const jsPDF = (await import("jspdf" as any)).jsPDF;
      const autoTable = (await import("jspdf-autotable" as any)).default;

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const bottomMargin = 20;
      let yOffset = margin;

      // Helper function to check if content fits on current page
      const checkPageSpace = (requiredHeight: number) => {
        if (yOffset + requiredHeight > pageHeight - bottomMargin) {
          pdf.addPage();
          yOffset = margin;
          return true; // Page was added
        }
        return false; // Content fits on current page
      };

      // Add logo and title
      try {
        const logo = new Image();
        logo.src = "/images/dr-logo.png";
        await new Promise((resolve, reject) => {
          logo.onload = resolve;
          logo.onerror = reject;
        });

        const logoWidth = 30;
        const logoHeight = (logo.height * logoWidth) / logo.width;
        const logoX = pageWidth / 2 - logoWidth / 2;

        pdf.addImage(logo.src, "PNG", logoX, yOffset, logoWidth, logoHeight);
        yOffset += logoHeight + 8;
      } catch (error) {
        console.log("Logo could not be loaded, continuing without it");
      }

      pdf.setFontSize(16);
      pdf.text("Reports", pageWidth / 2, yOffset, { align: "center" });
      yOffset += 15;

      // Add Status Counts Table
      if (reportsData?.status_counts) {
        // Estimate table height (rows * average row height + header + margins)
        const estimatedTableHeight = 10 * 7 + 10 + 10; // 10 rows * 7mm per row + header + margins
        checkPageSpace(estimatedTableHeight + 20); // +20 for title and total count

        pdf.setFontSize(14);
        pdf.text("Application Status Summary", 10, yOffset);
        yOffset += 7;

        const statusTableData = [
          [t("reports.statuses.screened"), reportsData.status_counts.screened || 0],
          [t("reports.statuses.rejected"), reportsData.status_counts.rejected || 0],
          [t("reports.statuses.aiInterview"), reportsData.status_counts.send_ai_interview || 0],
          [t("reports.statuses.scheduledInterview"), reportsData.status_counts.schedule_interview || 0],
          [t("reports.statuses.candidateAccept"), reportsData.status_counts.candidate_accept || 0],
          [t("reports.statuses.candidateReject"), reportsData.status_counts.candidate_reject || 0],
          [t("reports.statuses.interviewDone"), reportsData.status_counts.interview_done || 0],
          [t("reports.statuses.finalDecision"), reportsData.status_counts.final_decision || 0],
          [t("reports.statuses.hired"), reportsData.status_counts.hired || 0],
          [t("reports.statuses.interviewRejected"), reportsData.status_counts.interview_rejected || 0],
        ];

        autoTable(pdf, {
          startY: yOffset,
          head: [["Status", "Count"]],
          body: statusTableData,
          theme: "grid",
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
          margin: { left: 10, right: 10 },
        });

        yOffset = (pdf as any).lastAutoTable.finalY + 10;

        // Check space for total count
        checkPageSpace(10);
        pdf.setFontSize(12);
        pdf.text(`Total Applications: ${reportsData.total_count || 0}`, 10, yOffset);
        yOffset += 10;
      }

      // Capture and add pie chart if it exists
      if (pieChartRef.current && pieChartDataToDisplay && pieChartDataToDisplay.length > 0) {
        const pieCanvas = await html2canvas(pieChartRef.current, {
          scale: 2,
          backgroundColor: "#ffffff",
        });

        const pieImgData = pieCanvas.toDataURL("image/png");
        const pieImgWidth = pageWidth - 20;
        const pieImgHeight = (pieCanvas.height * pieImgWidth) / pieCanvas.width;

        // Check if pie chart fits on current page (title + chart + margin)
        const pieChartTotalHeight = 7 + pieImgHeight + 10;
        checkPageSpace(pieChartTotalHeight);

        pdf.setFontSize(14);
        pdf.text("Status Distribution Chart", 10, yOffset);
        yOffset += 7;

        pdf.addImage(pieImgData, "PNG", 10, yOffset, pieImgWidth, pieImgHeight);
        yOffset += pieImgHeight + 10;
      }

      // Add Daily Applications Table
      if (lineChartData && lineChartData.length > 0) {
        // Estimate table height
        const estimatedDailyTableHeight = lineChartData.length * 7 + 10 + 10;
        checkPageSpace(estimatedDailyTableHeight + 7); // +7 for title

        pdf.setFontSize(14);
        pdf.text("Daily Applications Summary", 10, yOffset);
        yOffset += 7;

        const dailyTableData = lineChartData.map((item) => [item.date, item.personalInterview]);

        autoTable(pdf, {
          startY: yOffset,
          head: [["Date", "Applications"]],
          body: dailyTableData,
          theme: "grid",
          headStyles: { fillColor: [52, 152, 219], textColor: 255 },
          margin: { left: 10, right: 10 },
        });

        yOffset = (pdf as any).lastAutoTable.finalY + 10;
      }

      // Capture and add line chart
      if (lineChartRef.current) {
        const lineCanvas = await html2canvas(lineChartRef.current, {
          scale: 2,
          backgroundColor: "#ffffff",
        });

        const lineImgData = lineCanvas.toDataURL("image/png");
        const lineImgWidth = pageWidth - 20;
        const lineImgHeight = (lineCanvas.height * lineImgWidth) / lineCanvas.width;

        // Check if line chart fits on current page (title + chart)
        const lineChartTotalHeight = 7 + lineImgHeight + 10;
        checkPageSpace(lineChartTotalHeight);

        pdf.setFontSize(14);
        pdf.text("Daily Applications Trend", 10, yOffset);
        yOffset += 7;

        pdf.addImage(lineImgData, "PNG", 10, yOffset, lineImgWidth, lineImgHeight);
      }

      // Save the PDF
      pdf.save(`reports_${new Date().toISOString().split("T")[0]}.pdf`);
      console.log("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
    }
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      <div className="reports-main">
        {/* Header */}
        <div className="reports-header">
          <h1 className="reports-title">{t("reports.title")}</h1>
          <div className="export-buttons">
            <button
              className="export-btn excel-btn d-none"
              onClick={handleExportExcel}
              title={t("reports.export.excel")}
            >
              <span className="export-icon"></span>
            </button>
            <button
              className="export-btn pdf-btn"
              onClick={handleExportPDF}
              // title={t("reports.export.pdf")}
            >
              <span className="export-icon"></span>
              <span style={{ paddingLeft: "5px" }}>{t("reports.export.pdf")}</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="reports-filters">
          <div className="filter-group">
            <label>{t("reports.filters.jobStatus")}</label>
            <select
              value={jobStatusFilter}
              onChange={(e) => {
                console.log("Job status dropdown changed to:", e.target.value);
                setJobStatusFilter(e.target.value);
                handleFilterChange("jobType", "");
              }}
              className="filter-select"
            >
              <option value="">{t("reports.filters.jobStatusPlaceholder")}</option>
              <option value="active">{t("reports.filters.jobStatusActive")}</option>
              <option value="expired">{t("reports.filters.jobStatusExpired")}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t("reports.filters.activeJobs")}</label>
            <select
              value={filters.jobType}
              onChange={(e) => handleFilterChange("jobType", e.target.value)}
              className="filter-select"
            >
              <option value="">{t("reports.filters.activeJobsPlaceholder")}</option>
              {filteredJobs.map((job: any) => (
                <option key={job.emp_job_id} value={job.emp_job_id}>
                  {job.job_title}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group d-none">
            <label>{t("reports.filters.allLocation")}</label>
            <select
              value={filters.location}
              onChange={(e) => handleFilterChange("location", e.target.value)}
              className="filter-select"
            >
              <option value="">{t("reports.filters.locationPlaceholder")}</option>
              <option value="remote">{t("reports.filters.locationOptions.remote")}</option>
              <option value="onsite">{t("reports.filters.locationOptions.onsite")}</option>
              <option value="hybrid">{t("reports.filters.locationOptions.hybrid")}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t("reports.filters.from")}</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              onClick={(e) => {
                try {
                  const input = e.currentTarget as any;
                  if (input.showPicker) {
                    input.showPicker();
                  }
                } catch (error) {
                  // Fallback for browsers that don't support showPicker
                  console.log("showPicker not supported");
                }
              }}
              className="filter-date"
              placeholder={t("reports.filters.datePlaceholder")}
            />
          </div>

          <div className="filter-group">
            <label>{t("reports.filters.to")}</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              onClick={(e) => {
                try {
                  const input = e.currentTarget as any;
                  if (input.showPicker) {
                    input.showPicker();
                  }
                } catch (error) {
                  // Fallback for browsers that don't support showPicker
                  console.log("showPicker not supported");
                }
              }}
              className="filter-date"
              placeholder={t("reports.filters.datePlaceholder")}
            />
          </div>

          <button className="btn-preview" onClick={handleFilter}>
            {t("reports.filters.findButton")}
          </button>
        </div>

        {/* Charts Section */}
        <div className="reports-charts">
          {/* Pie Chart */}
          {/* {pieChartDataToDisplay && pieChartDataToDisplay.length > 0 && ( */}
          <div ref={pieChartRef} className="chart-container pie-chart-container">
            <ReportsPieChart data={pieChartDataToDisplay} />
          </div>
          {/* )} */}

          {/* Line Chart */}
          <div ref={lineChartRef} className="chart-container line-chart-container">
            <ReportsChart data={lineChartData} />
          </div>
        </div>
      </div>
    </div>
  );
}
